import { hasElectronPreload } from '@renderer/lib/runtime/bridge'
import { markRendererCleanExit } from '@renderer/utils/telemetry/rendererLifecycle'
import {
  GRACEFUL_CLOSE_ACK,
  GRACEFUL_CLOSE_REQUEST,
  RENDERER_RELEASE_BUDGET_MS,
  type GracefulCloseAck,
  type GracefulCloseRequest
} from '../../../../shared/gracefulClose'

/**
 * Renderer half of the camera goodbye (see src/shared/gracefulClose.ts).
 *
 * Main asks (`app:will-close`) at the one moment the renderer is still alive — inside
 * the window `close` event, before the process is torn down. We release every camera
 * (libgphoto2's `gp_camera_exit`: Canon SetRemoteMode(1) + CloseSession, then the USB
 * device closed) and answer with the same requestId. Main waits a bounded time and then
 * closes regardless, so the two invariants here are:
 *
 * 1. ALWAYS ack, even when a release throws or overruns — a missing ack only costs
 *    main its full wait; a prompt ack lets the window close the moment we are done.
 * 2. Never take longer than our own budget, which is set below main's, so the ack
 *    arrives before main gives up.
 *
 * An older shell never sends the request, so on un-updated booths this is inert.
 */

interface GracefulQuitApi {
  on: <T>(channel: string, callback: (data: T) => void) => void
  send: (channel: string, data?: unknown) => void
}

export interface InstallOptions {
  /** Test seam; defaults to the preload bridge. */
  api?: GracefulQuitApi
  /** Test seam; defaults to unifiedCameraUtils.releaseAllForQuit. Resolves the count. */
  release?: (budgetMs: number) => Promise<number>
  /** Test seam. */
  now?: () => number
}

let installed = false

/** Where the last close's outcome is parked for the next boot to report. */
const LAST_GOODBYE_KEY = 'lastCameraGoodbye'

interface LastGoodbye {
  released: number
  ms: number
  at: number
}

function rememberGoodbye(entry: LastGoodbye): void {
  try {
    window.localStorage.setItem(LAST_GOODBYE_KEY, JSON.stringify(entry))
  } catch {
    // Private mode / storage disabled — diagnostics only, never worth failing the close.
  }
}

/**
 * Report the PREVIOUS close's camera release into this session's activity log.
 *
 * It cannot be logged as it happens: the renderer console is never uploaded and the
 * activity-log flush would not survive the process exiting. Parking it and reporting it
 * on the next boot is what makes "did the goodbye actually run?" answerable from the
 * fleet logs instead of from someone listening for the shutter click.
 */
export async function reportPreviousGoodbye(): Promise<void> {
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(LAST_GOODBYE_KEY)
  } catch {
    return
  }
  if (!raw) return
  const forget = (): void => {
    try {
      window.localStorage.removeItem(LAST_GOODBYE_KEY)
    } catch {
      // ignore
    }
  }
  let entry: LastGoodbye
  try {
    entry = JSON.parse(raw) as LastGoodbye
  } catch {
    forget() // malformed — do not retry it forever
    return
  }

  const { useSessionStateStore } = await import('@renderer/stores/sessionState')
  // Guard first, log second. `logActivity` WRITES to the session store, so anything that
  // reacts to that store and logs from inside the reaction re-enters itself: the store
  // notifies, we log, the log sets state, the store notifies… until the stack blows.
  // That is exactly what shipped and broke live booths on 2026-09-16 ("Maximum call
  // stack size exceeded", which then surfaced on unrelated calls like the credits
  // check). The flag is set BEFORE the log so any re-entry is a no-op.
  let reported = false
  const emit = (): boolean => {
    if (reported) return true
    const session = useSessionStateStore.getState()
    // The activity log only exists inside a session; at boot there is not one yet.
    if (!session.id) return false
    reported = true
    forget()
    try {
      session.logActivity(
        entry.released > 0
          ? 'Goodbye: camera released when the app last closed'
          : 'Goodbye: the app last closed with no camera to release',
        { released: entry.released, ms: entry.ms, agoMs: Date.now() - entry.at },
        'capture'
      )
    } catch {
      // A diagnostic line must never take the app down with it.
    }
    return true
  }
  if (emit()) return
  // Poll for the first session rather than subscribing to the store: a store
  // subscription is the reentrancy trap described above, and nothing here is worth that
  // risk. Gives up quietly — a missed diagnostic line costs nothing.
  const deadline = Date.now() + 60_000
  const tick = (): void => {
    if (emit() || Date.now() > deadline) return
    setTimeout(tick, 2000)
  }
  setTimeout(tick, 2000)
}

/** Head-room so our ack lands inside main's wait even after IPC latency. */
const ACK_HEADROOM_MS = 300

async function defaultRelease(budgetMs: number): Promise<number> {
  // Lazy so this module stays light for the boot path and unit tests.
  const { unifiedCameraUtils } = await import('./uniCamUtils')
  return unifiedCameraUtils.releaseAllForQuit(budgetMs)
}

/**
 * Register the goodbye handler once. Returns true when installed, false when there is
 * no Electron bridge (web/Tauri builds) or it was already installed.
 */
export function installGracefulQuitHandler(options: InstallOptions = {}): boolean {
  if (installed) return false
  const api =
    options.api ??
    (hasElectronPreload() && typeof window.api?.on === 'function'
      ? (window.api as unknown as GracefulQuitApi)
      : null)
  if (!api) return false
  installed = true

  const release = options.release ?? defaultRelease
  const now = options.now ?? ((): number => Date.now())

  api.on<GracefulCloseRequest>(GRACEFUL_CLOSE_REQUEST, (req) => {
    // Main destroys the window after this, which skips `pagehide`: without this
    // mark every ordinary quit would read as a crash on the next boot.
    markRendererCleanExit('app_close')
    const startedAt = now()
    const requestId = req?.requestId ?? ''
    const budget = Math.max(
      200,
      Math.min(RENDERER_RELEASE_BUDGET_MS, (req?.budgetMs ?? RENDERER_RELEASE_BUDGET_MS) - ACK_HEADROOM_MS)
    )
    let acked = false
    const ack = (released: number): void => {
      if (acked) return
      acked = true
      const payload: GracefulCloseAck = { requestId, released, ms: now() - startedAt }
      rememberGoodbye({ released, ms: payload.ms, at: Date.now() })
      try {
        api.send(GRACEFUL_CLOSE_ACK, payload)
      } catch {
        // The bridge is gone; nothing left to tell.
      }
    }
    // Invariant 2: overrunning our budget still acks (with what we managed).
    const guard = setTimeout(() => ack(0), budget)
    void release(budget)
      .then((released) => ack(released))
      .catch(() => ack(0)) // Invariant 1: a failed release still acks.
      .finally(() => clearTimeout(guard))
  })
  return true
}

/** Test seam. */
export function resetGracefulQuitHandlerForTests(): void {
  installed = false
}
